import { MODAL_CALCULATOR_PROPS } from 'src/modules/user-input/components/FlowCalculatorModals.tsx';
import { MediumParams } from 'src/modules/user-input/components/MediumParams.tsx';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { DecimalField } from 'src/modules/common/components/DecimalField/DecimalField.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { MediumParamsChangeEventDetails } from 'src/modules/user-input/MediumParams/types.ts';
import { convert } from 'src/modules/user-input/convert.ts';
import { CalculatorModalLayout } from 'src/modules/common/layouts/CalculatorModalLayout.tsx';
import {
    CalculatorModalTopSection,
} from 'src/modules/common/components/CalculatorModalTopSection/CalculatorModalTopSection';
import {
    CalculatorModalMainSection,
} from 'src/modules/common/components/CalculatorModalMainSection/CalculatorModalMainSection.tsx';
import { Units } from 'uom-units';
import { reducer } from 'src/modules/user-input/calcFtHead/reducer.ts';
import { initialState } from 'src/modules/user-input/calcFtHead/initialState.ts';
import { calcPressureFromHeight } from 'src/modules/user-input/calcFtHead/calcPressureFromHeight.ts';
import { numberFormatter } from 'src/utils/numberFormatter.ts';
import { UnitSelector } from './UnitSelector';

export const CalcFtHead = ({
    handleModalValueChange,
    currentLocale,
    currentRegion,
    allowedUnits,
    targetUnit,
    uuid,
    title,
}: MODAL_CALCULATOR_PROPS) => {
    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
    });

    const onMediumParamsChange = useCallback((mediumParams: MediumParamsChangeEventDetails) => {
        dispatch({
            type: 'mediumParams_change',
            payload: mediumParams,
        });
    }, [dispatch]);

    const maxDiffPressure = useMemo(() => {
        try {
            return convert(
                calcPressureFromHeight(
                    convert(state.ftHead, Units.Foot, Units.Meter),
                    convert(state.density, Units.PoundPerCubicFoot, Units.KilogramPerCubicMeter),
                ),
                Units.Pascal,
                targetUnit,
            );
        } catch (e) {
            return '';
        }
    }, [state, targetUnit]);

    useEffect(() => {
        handleModalValueChange(maxDiffPressure);
    }, [maxDiffPressure, handleModalValueChange]);

    return (
        <CalculatorModalLayout>
            <CalculatorModalTopSection>
                <MediumParams
                    uuid={uuid}
                    currentRegion={currentRegion}
                    allowedUnits={allowedUnits}
                    currentLocale={currentLocale}
                    onChange={onMediumParamsChange}
                    showHeatCapacity={false}
                />
            </CalculatorModalTopSection>
            <CalculatorModalMainSection
                title={title}
                unit={targetUnit.name}
                result={numberFormatter(maxDiffPressure, currentLocale)}
                onClick={() => {
                    dispatch({ type: 'reset' });
                }}
            >
                <DecimalField
                    name="ftHead"
                    inputSize="lg"
                    labelText={MESSAGES.FT_HEAD}
                    value={state.ftHead}
                    endAdornment={(
                        <UnitSelector
                            values={[Units.Foot]}
                            selected={Units.Foot.name}
                            onChange={() => null}
                        />
                    )}
                    currentLocale={currentLocale}
                    onValueChange={(payload) => {
                        dispatch({
                            type: 'ftHead_change',
                            payload,
                        });
                    }}
                    autoComplete="off"
                />
            </CalculatorModalMainSection>
        </CalculatorModalLayout>
    );
};
